import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  fetchIntegrityStats,
  INTEGRITY_PRINCIPLES,
  integrityStatusMeta,
  listIntegrityChecks,
  RULE_TYPES,
  ruleTypeMeta,
  runIntegrityChecks,
  type CoreIntegrityCheck,
} from '@/lib/coreIntegrity';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

export default function IntegrityTab({ org }: Props) {
  const [checks, setChecks] = useState<CoreIntegrityCheck[]>([]);
  const [stats, setStats] = useState({ total: 0, passed: 0, failed: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [c, s] = await Promise.all([listIntegrityChecks(), fetchIntegrityStats()]);
      setChecks(c);
      setStats(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setRunning(true);
    setError('');
    setNotice('');
    try {
      const res = await runIntegrityChecks(org.id);
      setNotice(`Vérification terminée : ${res.passed} conforme(s), ${res.failed} anomalie(s).`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Exécution impossible.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Data integrity</span> : contraintes, foreign keys,
        validations, unique constraints et indexes. Exemple : un rendez-vous ne peut pas pointer vers un
        professionnel inexistant, une commande ne peut pas référencer un client supprimé de façon incohérente.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}
      {notice && <div className="p-3 bg-secondary-50 border border-secondary-200 rounded-md text-xs text-secondary-800 flex items-center justify-between"><span>{notice}</span><button onClick={() => setNotice('')} className="cursor-pointer"><i className="ri-close-line"></i></button></div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-shield-check-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Contrôles</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-checkbox-circle-line text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.passed}</p>
          <p className="text-xs text-foreground-500">Conformes</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-error-warning-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.failed}</p>
          <p className="text-xs text-foreground-500">Anomalies</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-time-line text-lg text-foreground-500"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.pending}</p>
          <p className="text-xs text-foreground-500">À vérifier</p>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-node-tree text-primary-600"></i>Principes appliqués au schéma</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
          {INTEGRITY_PRINCIPLES.map((p) => (
            <div key={p.key} className="rounded-lg border border-background-200/70 bg-background-100/60 p-3">
              <p className="text-xs font-medium text-foreground-800 flex items-center gap-2"><i className={`${p.icon} text-primary-600`}></i>{p.label}</p>
              <p className="text-[11px] text-foreground-500 mt-1">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-shield-check-line"></i></span>
            <h4 className="text-sm font-semibold text-foreground-900">Contrôles d’intégrité</h4>
          </div>
          <button onClick={run} disabled={running} className="px-4 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
            {running ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-play-line"></i>} Lancer la vérification
          </button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : checks.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucun contrôle défini.</p>
        ) : (
          <div className="divide-y divide-background-200/60">
            {checks.map((c) => {
              const rm = ruleTypeMeta(c.rule_type);
              const sm = integrityStatusMeta(c.status);
              return (
                <div key={c.id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                  <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-700 flex items-center justify-center flex-shrink-0"><i className={rm.icon}></i></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-800 flex items-center gap-2">
                      {c.label}
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900">{rm.label}</span>
                      {c.severity === 'error' && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-800">critique</span>}
                    </p>
                    <p className="text-[11px] text-foreground-400 truncate">{c.target_table} · {c.message}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${sm.className}`}>{sm.label}</span>
                  <span className="text-[10px] text-foreground-400 whitespace-nowrap">{c.last_run_at ? new Date(c.last_run_at).toLocaleString('fr-FR') : 'jamais'}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-links-line text-accent-600"></i>Types de règles</h4>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {RULE_TYPES.map((r) => (
            <div key={r.key} className="rounded-lg border border-background-200/70 p-3">
              <p className="text-xs font-medium text-foreground-800 flex items-center gap-2"><i className={`${r.icon} text-primary-600`}></i>{r.label}</p>
              <p className="text-[11px] text-foreground-500 mt-1">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}